import React, { useState, useEffect } from 'react';
import Button from '../../components/common/button/Button';
import Modal from '../../components/common/model/Modal';
import styles from './CustomerList.module.scss';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import customerService from '../../services/customerService';
import InputField from '../../components/common/inputField/InputField';
import { Customer } from '../../types/customerTypes';

const CustomerList: React.FC<{ role?: string }> = ({ role = 'admin' }) => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: 'Tamilnadu',
    zip_code: '',
    country: 'India',
    gst: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [editingCustomerId, setEditingCustomerId] = useState<string | null>(null);

  const fetchCustomers = async () => {
    try {
      const response = await customerService.getCustomers();

      const normalizedCustomers: Customer[] = Array.isArray(response)
        ? response
            .map((customer): Customer | null => {
              if (!customer || typeof customer !== 'object') {
                console.warn('Invalid customer object:', customer);
                return null;
              }

              return {
                id: customer.id ?? '',
                name: customer.name ?? '',
                email: customer.email ?? '',
                phone: customer.phone ?? '',
                address: customer.address,
                city: customer.city,
                state: customer.state,
                zip_code: customer.zip_code,
                country: customer.country,
                gst: customer.gst,
                employee_id: customer.employee_id ?? '',
                employee_name: customer.employee_name,
                is_active: customer.is_active ?? true,
                created_at: customer.created_at ?? new Date().toISOString(),
                updated_at: customer.updated_at ?? new Date().toISOString(),
              };
            })
            .filter((customer): customer is Customer => customer !== null)
        : [];

      setCustomers(normalizedCustomers);
    } catch (err) {
      console.error('Error fetching customers:', err);
      toast.error('Failed to fetch customers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, []);

  const handleAdd = async () => {
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
      toast.error('Please fill in all required fields: Name, Email, and Phone.');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.email)) {
      toast.error('Please enter a valid email address.');
      return;
    }
    
    const data = {
      name: form.name,
      email: form.email,
      phone: form.phone,
      address: form.address || undefined,
      city: form.city || undefined,
      state: form.state || undefined,
      zip_code: form.zip_code || undefined,
      country: form.country || undefined,
      gst: form.gst || undefined
    };

    try {
      await customerService.createCustomer(data); 
      toast.success('Customer created successfully!');
      setShowModal(false);
      resetForm();
      fetchCustomers(); 
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create customer';
      toast.error(message);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this customer?')) {
      return;
    }

    try {
      await customerService.deleteCustomer(id);
      toast.success('Customer deleted successfully!');
      fetchCustomers();
    } catch (err) {
      toast.error('Failed to delete customer');
    }
  };

  const handleToggleActive = async (id: string) => {
    const currentCustomer = customers.find(customer => customer.id === id);
    if (!currentCustomer) {
      toast.error('Customer not found');
      return;
    }

    const currentStatus = currentCustomer.is_active ?? true;
    const newStatus = !currentStatus;
    
    setCustomers(customers.map(customer =>
      customer.id === id ? { ...customer, is_active: newStatus } : customer
    ));

    try {
      const updatedCustomer = await customerService.toggleCustomerStatus(id);
      
      if (updatedCustomer && typeof updatedCustomer === 'object') {
        setCustomers(prevCustomers => prevCustomers.map(customer => {
          if (customer.id === id) {
            return {
              ...customer,
              ...updatedCustomer,
              is_active: updatedCustomer.is_active ?? newStatus,
            };
          }
          return customer;
        }));
      }
      
      toast.success('Customer status updated successfully!');
    } catch (err) {
      setCustomers(prevCustomers => prevCustomers.map(customer =>
        customer.id === id ? { ...customer, is_active: currentStatus } : customer
      ));
      
      const message = err instanceof Error ? err.message : 'Failed to update customer status';
      toast.error(message);
    }
  };

  const resetForm = () => {
    setForm({
      name: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      state: '',
      zip_code: '',
      country: '',
      gst: ''
    });
  };

  const handleOpenModal = () => {
    setShowModal(true);
  };

  const handleOpenEditModal = (customer: Customer) => {
    setForm({
      name: customer.name || '',
      email: customer.email || '',
      phone: customer.phone || '',
      address: customer.address || '',
      city: customer.city || '',
      state: customer.state || '',
      zip_code: customer.zip_code || '',
      country: customer.country || '',
      gst: customer.gst || ''
    });
    setEditingCustomerId(customer.id);
    setEditMode(true);
    setShowModal(true);
  };

  const handleSaveEdit = async () => {
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
      toast.error('Please fill in all required fields: Name, Email, and Phone.');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.email)) {
      toast.error('Please enter a valid email address.');
      return;
    }

    if (!editingCustomerId) return;
    
    const data = {
      id: editingCustomerId,
      name: form.name,
      email: form.email,
      phone: form.phone,
      address: form.address || undefined,
      city: form.city || undefined,
      state: form.state || undefined,
      zip_code: form.zip_code || undefined,
      country: form.country || undefined,
      gst: form.gst || undefined
    };
    
    try {
      await customerService.updateCustomer(data);
      toast.success('Customer updated successfully!');
      setShowModal(false);
      setEditMode(false);
      setEditingCustomerId(null);
      resetForm();
      fetchCustomers();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update customer';
      toast.error(message);
    }
  };

  const filteredCustomers = customers.filter(customer =>
    (customer.name?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
    (customer.email?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
    (customer.phone?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
    (customer.employee_name?.toLowerCase() || '').includes(searchTerm.toLowerCase())
  );

  return (
    <div className={styles.container}>
      <h4>Customers</h4>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <div className={styles.searchContainer}>
            <div className={styles.searchWrapper}>
              <svg className={styles.searchIcon} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.35-4.35"></path>
              </svg>
              <input
                type="text"
                placeholder="Search customers by name, email, phone ..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={styles.searchInput}
              />
              {searchTerm && (
                <button
                  className={styles.clearButton}
                  onClick={() => setSearchTerm('')}
                  aria-label="Clear search"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>
        <div className={styles.headerRight}>
          {role === 'admin' && (
            <Button variant="primary" onClick={handleOpenModal}>
              + Add Customer
            </Button>
          )}
        </div>
      </div>

      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Phone</th>
              <th>City</th>
              <th>GST</th>
              <th>Created By</th>
              {role === 'admin' && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={role === 'admin' ? 7 : 6} style={{ textAlign: 'center', padding: '20px' }}>
                  Loading customers...
                </td>
              </tr>
            ) : (
              Array.isArray(filteredCustomers) && filteredCustomers.length > 0 ? (
                filteredCustomers.map(customer => {
                  if (!customer || !customer.id) {
                    console.warn('Invalid customer in render:', customer);
                    return null;
                  }
                  
                  const isActive = customer.is_active !== undefined ? customer.is_active : true;
                  
                  return (
                    <tr key={customer.id} className={!isActive ? styles.inactive : ''}>
                      <td data-label="Name">{customer.name || '-'}</td>
                      <td data-label="Email">{customer.email || '-'}</td>
                      <td data-label="Phone">{customer.phone || '-'}</td>
                      <td data-label="City">{customer.city || '-'}</td>
                      <td data-label="GST">{customer.gst || '-'}</td>
                      <td data-label="Created By">{customer.employee_name || '-'}</td>
                      {role === 'admin' && (
                        <td data-label="Actions">
                          <div className={styles.actionIcons}>
                            <Button
                              variant={isActive ? 'secondary' : 'success'}
                              className={styles.actionButton}
                              onClick={() => handleToggleActive(customer.id)}
                              aria-label={isActive ? 'Disable Customer' : 'Enable Customer'}
                            >
                              {isActive ? (
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <polyline points="20 6 9 17 4 12"></polyline>
                                </svg>
                              ) : (
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <line x1="18" y1="6" x2="6" y2="18"></line>
                                  <line x1="6" y1="6" x2="18" y2="18"></line>
                                </svg>
                              )}
                            </Button>
                            <Button
                              onClick={() => handleOpenEditModal(customer)}
                              className={styles.actionButton}
                              aria-label="Edit Customer"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M12 20h9"/>
                                <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19.5 3 21l1.5-4L16.5 3.5z"/>
                              </svg>
                            </Button>
                            <Button
                              onClick={() => handleDelete(customer.id)}
                              className={styles.actionButton}
                              aria-label="Delete Customer"
                            >
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="#ef4444"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <polyline points="3 6 5 6 21 6"></polyline>
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"></path>
                                <line x1="10" y1="11" x2="10" y2="17"></line>
                                <line x1="14" y1="11" x2="14" y2="17"></line>
                              </svg>
                            </Button>
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                }).filter(row => row !== null)
              ) : (
                <tr>
                  <td colSpan={role === 'admin' ? 7 : 6} style={{ textAlign: 'center', padding: '20px' }}>
                    {searchTerm ? 'No customers found matching your search' : 'No customers found'}
                  </td>
                </tr>
              )
            )}
          </tbody>
        </table>
      </div>
      
      <Modal 
        isOpen={showModal} 
        onClose={() => { 
          setShowModal(false); 
          setEditMode(false); 
          setEditingCustomerId(null); 
          resetForm(); 
        }} 
        title={editMode ? "Edit Customer" : "Add Customer"} 
        size='lg'
      >
        <div className={styles.modalForm}>
          <InputField
            label="Customer Name"
            name="name"
            type="text"
            value={form.name}
            onChange={e => setForm({ ...form, name: e.target.value })}
            placeholder="Enter customer name"
            required
          />
          <InputField
            label="Email"
            name="email"
            type="email"
            value={form.email}
            onChange={e => setForm({ ...form, email: e.target.value })}
            placeholder="Enter email address"
            required
          />
          <InputField
            label="Phone"
            name="phone"
            type="tel"
            value={form.phone}
            onChange={e => setForm({ ...form, phone: e.target.value })}
            placeholder="Enter phone number"
            required
          />
          <InputField
            label="Address"
            name="address"
            type="text"
            value={form.address}
            onChange={e => setForm({ ...form, address: e.target.value })}
            placeholder="Enter address"
          />
          <InputField
            label="City"
            name="city"
            type="text"
            value={form.city}
            onChange={e => setForm({ ...form, city: e.target.value })}
            placeholder="Enter city"
          />
          <InputField
            label="State"
            name="state"
            type="text"
            value={form.state}
            onChange={e => setForm({ ...form, state: e.target.value })}
            placeholder="Enter state"
          />
          <InputField
            label="Zip Code"
            name="zip_code"
            type="text"
            value={form.zip_code}
            onChange={e => setForm({ ...form, zip_code: e.target.value })}
            placeholder="Enter zip code"
          />
          <InputField
            label="Country"
            name="country"
            type="text"
            value={form.country}
            onChange={e => setForm({ ...form, country: e.target.value })}
            placeholder="Enter country"
          />
          <InputField
            label="GST Number"
            name="gst"
            type="text"
            value={form.gst}
            onChange={e => setForm({ ...form, gst: e.target.value })}
            placeholder="Enter GST number"
          />

          <div className={styles.modalActions}>
            <Button
              variant="secondary"
              onClick={() => {
                setShowModal(false);
                setEditMode(false);
                setEditingCustomerId(null);
                resetForm();
              }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={editMode ? handleSaveEdit : handleAdd}
              className={styles.submitButton}
            >
              {editMode ? 'Save Changes' : 'Add Customer'}
            </Button>
          </div>
        </div>
      </Modal>
      
      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

export default CustomerList;